import { Injectable } from '@angular/core';
import { BaseNotifier } from '../../../../shared/notifiers/base-notifier';
import { Ref } from '../../../../shared/providers/ref';
import { MapProvider } from '../map/map-provider';
import { BasemapSwitcherState } from './basemap-switcher-state';
import { BasemapConstant, BasemapProviderEnum } from '@gishub/domain';
import { BasemapLayerFactory } from '@gishub/infrastructure';
import { BasemapRepositoryProvider } from '../../../../shared/providers/repository-providers';
import WebTileLayer from '@arcgis/core/layers/WebTileLayer';
import VectorTileLayer from '@arcgis/core/layers/VectorTileLayer';
import Basemap from '@arcgis/core/Basemap';
import Layer from '@arcgis/core/layers/Layer';
import { BingTileLayer } from '@gishub/layers/bing-tile-layer';

@Injectable()
export class BasemapSwitcherNotifier extends BaseNotifier<BasemapSwitcherState> {
  private static readonly INITIAL: BasemapSwitcherState = {
    options: [],
    activeId: BasemapConstant.ARCGIS_LIGHT_GRAY,
    search: '',
  };

  constructor(ref: Ref) {
    super(ref, BasemapSwitcherNotifier.INITIAL);
  }

  override build(): void {
    const repo = this.ref.watch(BasemapRepositoryProvider).instance;
    repo.getAll().then(r => {
      if (r.success) {
        this.updateState(s => ({ ...s, options: r.data ?? [] }));
      }
    });
  }

  syncActive(id: string): void {
    this.updateState(s => ({ ...s, activeId: id }));
  }

  setActive(id: string): void {
    const view = this.ref.watch(MapProvider).state().view;
    if (!view) return;

    const option = this.snapshot().options.find(o => o.id === id);
    if (!option) return;

    this.setState({ ...this.snapshot(), activeId: id });

    const config = BasemapLayerFactory.createConfig(option);
    let baseLayer: Layer;

    if (option.type === 'vector') {
      baseLayer = new VectorTileLayer({ url: config.urlTemplate });
    } else if (option.provider === BasemapProviderEnum.BING) {
      baseLayer = new BingTileLayer({ urlTemplate: config.urlTemplate, subDomains: config.subDomains });
    } else {
      baseLayer = new WebTileLayer({
        urlTemplate: config.urlTemplate,
        subDomains: config.subDomains,
        copyright: config.attribution,
      });
    }

    if (view.map) {
      view.map.basemap = new Basemap({ baseLayers: [baseLayer] });
    }
  }
}
